import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { seoApi } from "@/lib/seoApi";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Sparkles, ExternalLink, Copy as CopyIcon, Layers } from "lucide-react";

export default function BriefList() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [scheduleAt, setScheduleAt] = useState("");
  const [batchBusy, setBatchBusy] = useState(false);
  const navigate = useNavigate();

  async function load() {
    const r = await seoApi.get("/seo/briefs?limit=200");
    setRows(r.data);
  }
  useEffect(() => { load(); }, []);

  const activeRows = rows.filter((r) => r.is_active);

  function toggle(id) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  function toggleAll() {
    setSelected((s) =>
      s.size === activeRows.length ? new Set() : new Set(activeRows.map((r) => r.id)));
  }

  async function deactivate(row) {
    if (!window.confirm(`Deactivate brief "${row.data.title}"?`)) return;
    try {
      await seoApi.post(
        `/seo/briefs/${row.id}/deactivate?change_reason=${encodeURIComponent("deactivated via admin UI")}`,
      );
      toast.success("Brief deactivated");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  }

  async function generate(row) {
    setBusy(row.id);
    try {
      toast.info("Queued — the sequential worker will pick it up…");
      const r = await seoApi.post("/seo/workflows/generate-from-brief", {
        brief_id: row.id,
      });
      navigate(`/seo-studio/runs/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(null);
    }
  }

  async function queueBatch() {
    setBatchBusy(true);
    try {
      const body = { brief_ids: [...selected] };
      if (scheduleAt) body.scheduled_at = new Date(scheduleAt).toISOString();
      const r = await seoApi.post("/seo/workflows/batch-generate", body);
      toast.success(`Batch queued: ${r.data.tasks_total} articles${scheduleAt ? " (scheduled)" : ""}`);
      setSelected(new Set());
      setScheduleAt("");
      navigate("/seo-studio/batches");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || err.message);
    } finally {
      setBatchBusy(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="briefs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Content Briefs</h1>
          <p className="text-neutral-500 text-sm mt-1">
            Structured input consumed by the AI Content Factory. Select several briefs to queue a batch.
          </p>
        </div>
        <Link to="/seo-studio/briefs/new">
          <Button data-testid="brief-new-btn">
            <Plus size={16} className="mr-1" /> New Brief
          </Button>
        </Link>
      </div>

      {selected.size > 0 && (
        <Card className="border-sky-200 bg-sky-50" data-testid="batch-bar">
          <CardContent className="p-4 flex items-center gap-4 flex-wrap">
            <span className="text-sm font-medium">
              <Layers size={15} className="inline mr-1" />
              Selected: {selected.size}
            </span>
            <label className="text-xs text-neutral-600 flex items-center gap-2">
              Schedule (optional, e.g. tonight):
              <Input type="datetime-local" value={scheduleAt}
                     onChange={(e) => setScheduleAt(e.target.value)}
                     className="h-8 w-52 text-sm bg-white" data-testid="batch-schedule-input" />
            </label>
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
            <Button size="sm" onClick={queueBatch} disabled={batchBusy}
                    data-testid="batch-queue-btn">
              <Layers size={14} /> {batchBusy ? "Queueing…" : `Queue batch (${selected.size})`}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="text-sm text-neutral-500 p-10 text-center">
              No briefs yet — create your first one to start generating articles.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-neutral-500">
                <tr className="border-b border-neutral-200">
                  <th className="px-4 py-3 w-8">
                    <input type="checkbox"
                           checked={activeRows.length > 0 && selected.size === activeRows.length}
                           onChange={toggleAll}
                           data-testid="batch-select-all" />
                  </th>
                  <th className="text-left px-2 py-3">Title</th>
                  <th className="text-left px-4 py-3">Primary keyword</th>
                  <th className="text-left px-4 py-3">Length</th>
                  <th className="text-left px-4 py-3">Provider</th>
                  <th className="text-left px-4 py-3">Version</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-neutral-100" data-testid={`brief-row-${row.id}`}>
                    <td className="px-4 py-3">
                      <input type="checkbox" disabled={!row.is_active}
                             checked={selected.has(row.id)}
                             onChange={() => toggle(row.id)}
                             data-testid={`batch-select-${row.id}`} />
                    </td>
                    <td className="px-2 py-3">
                      <Link to={`/seo-studio/briefs/${row.id}`} className="hover:underline font-medium">
                        {row.data.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-neutral-600">{row.data.primary_keyword || "—"}</td>
                    <td className="px-4 py-3 text-neutral-600">
                      {row.data.min_words}–{row.data.max_words} words
                    </td>
                    <td className="px-4 py-3 text-neutral-600 text-xs">
                      {row.data.provider || "—"}{row.data.model ? ` / ${row.data.model}` : ""}
                    </td>
                    <td className="px-4 py-3">v{row.current_version}</td>
                    <td className="px-4 py-3">
                      {row.is_active
                        ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" variant="outline">active</Badge>
                        : <Badge className="bg-neutral-100 text-neutral-600 border-neutral-200" variant="outline">inactive</Badge>}
                    </td>
                    <td className="px-6 py-3 text-right space-x-2 whitespace-nowrap">
                      <Button size="sm" onClick={() => generate(row)}
                              disabled={!row.is_active || busy === row.id}
                              data-testid={`generate-${row.id}`}>
                        <Sparkles size={14} /> {busy === row.id ? "…" : "Generate"}
                      </Button>
                      <Link to={`/seo-studio/briefs/${row.id}/edit`}>
                        <Button size="sm" variant="outline" data-testid={`edit-${row.id}`}>
                          <Pencil size={14} />
                        </Button>
                      </Link>
                      <Link to={`/seo-studio/briefs/new?copy_from=${row.id}`}>
                        <Button size="sm" variant="outline" data-testid={`duplicate-${row.id}`}
                                title="Duplicate this brief">
                          <CopyIcon size={14} />
                        </Button>
                      </Link>
                      <Button size="sm" variant="outline" onClick={() => deactivate(row)}
                              disabled={!row.is_active}
                              data-testid={`deactivate-${row.id}`}>
                        <Trash2 size={14} />
                      </Button>
                      <Link to={`/seo-studio/briefs/${row.id}`}
                            className="inline-flex items-center gap-1 text-xs text-neutral-600 hover:text-neutral-900 ml-2">
                        Details <ExternalLink size={12} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
